import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDown, GraduationCap, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { KineticLine, Reveal, Overline, EASE } from "../components/motion";
import { PhotoGallery } from "../components/PhotoGallery";
import { IMAGES, PROGRAMS, MANIFESTO, STATS, TESTIMONIALS, NEWS } from "../data/content";

const leadInputCls =
  "w-full bg-transparent border border-border rounded-sm px-3.5 py-2.5 text-sm text-navy placeholder:text-muted-foreground/60 focus:outline-none focus:border-accent transition-colors duration-300";

const COUNTRY_CODES = [
  ["+1", "Canada / USA"], ["+91", "India"], ["+52", "Mexico"], ["+44", "United Kingdom"],
  ["+61", "Australia"], ["+63", "Philippines"], ["+86", "China"], ["+81", "Japan"],
  ["+49", "Germany"], ["+33", "France"], ["+55", "Brazil"], ["+27", "South Africa"],
];

const HeroLeadForm = () => {
  const [form, setForm] = useState({ email: "", code: "+1", phone: "", city: "", program: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        email: form.email,
        phone: `${form.code} ${form.phone}`.trim(),
        city: form.city,
        program: form.program,
        message: "Lead captured from the homepage hero form.",
      });
      toast.success("Thank you — our admissions team will reach out shortly.");
      setForm({ email: "", code: "+1", phone: "", city: "", program: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.form
      onSubmit={submit}
      data-testid="hero-lead-form"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 1.35, ease: EASE }}
      className="relative w-full max-w-md ml-auto bg-background/95 backdrop-blur-md border border-gold/40 rounded-sm p-7 shadow-[0_30px_60px_-15px_rgba(10,17,40,0.5)]"
    >
      <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold via-accent to-gold rounded-t-sm" />
      <p className="font-serif text-2xl text-navy leading-tight">Get Program Details</p>
      <p className="mt-1 text-xs text-muted-foreground">Free, no-obligation info from admissions.</p>
      <div className="mt-4 space-y-3">
        <input
          required
          type="email"
          data-testid="hero-lead-email"
          className={leadInputCls}
          placeholder="Email address"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <select
          data-testid="hero-lead-country-code"
          className={leadInputCls}
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
        >
          {COUNTRY_CODES.map(([code, label]) => (
            <option key={code} value={code}>{code} {label}</option>
          ))}
        </select>
        <input
          required
          type="tel"
          data-testid="hero-lead-phone"
          className={leadInputCls}
          placeholder="Phone number"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <input
          required
          data-testid="hero-lead-city"
          className={leadInputCls}
          placeholder="City"
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
        />
        <select
          data-testid="hero-lead-program"
          className={leadInputCls}
          value={form.program}
          onChange={(e) => setForm({ ...form, program: e.target.value })}
        >
          <option value="">Program of interest…</option>
          {PROGRAMS.map((p) => (
            <option key={p.id} value={p.title}>{p.title}</option>
          ))}
          <option value="General Enquiry">General Enquiry</option>
        </select>
        <button
          type="submit"
          disabled={sending}
          data-testid="hero-lead-submit"
          className="w-full inline-flex items-center justify-center gap-2 bg-navy text-primary-foreground py-3 rounded-sm text-sm tracking-wide transition-colors duration-300 hover:bg-accent disabled:opacity-60"
        >
          {sending ? "Sending…" : "Request Info"}
          <Send size={14} />
        </button>
      </div>
    </motion.form>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.img
        src={IMAGES.hero}
        alt="Mountain landscape — the Victoria Career College classroom"
        data-testid="hero-image"
        style={{ y: imgY, scale: 1.18 }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/60 to-navy/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/40" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full grid lg:grid-cols-12 gap-12 items-center pt-36 pb-28">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Overline light>A private career college · Surrey, British Columbia</Overline>
          </motion.div>
          <h1 className="mt-6 font-serif font-medium tracking-tight leading-[1.05] text-primary-foreground text-5xl sm:text-6xl lg:text-7xl">
            <KineticLine delay={0.35}>Small classrooms.</KineticLine>
            <KineticLine delay={0.5}>
              <span className="italic text-gold">Big thinking.</span>
            </KineticLine>
            <KineticLine delay={0.65}>Real careers.</KineticLine>
          </h1>
          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-primary-foreground/75"
          >
            Victoria Career College delivers professional, industry-recognised training
            in classes of four to six students — where hands-on learning and critical
            thinking turn ambition into expertise.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Link
              to="/programs"
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-2 bg-gold text-navy px-7 py-3.5 rounded-sm text-sm tracking-wide font-medium transition-colors duration-300 hover:bg-primary-foreground"
            >
              Explore Programs
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link
              to="/about"
              data-testid="hero-story-link"
              className="group text-sm tracking-wide text-primary-foreground inline-flex items-center gap-2"
            >
              <span className="border-b border-gold pb-0.5 transition-colors duration-300 group-hover:text-gold">
                Our Philosophy
              </span>
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.25, ease: EASE }}
            className="mt-10 inline-flex items-center gap-3 border border-primary-foreground/25 bg-navy/30 backdrop-blur-sm rounded-sm px-4 py-3"
            data-testid="hero-badge"
          >
            <GraduationCap className="text-gold" size={20} />
            <div>
              <p className="font-serif text-base leading-none text-primary-foreground">PTIRU Certified</p>
              <p className="text-[10px] tracking-[0.15em] uppercase text-primary-foreground/60 mt-1">Government of British Columbia</p>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9, ease: EASE }}
          className="lg:col-span-5"
        >
          <HeroLeadForm />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-primary-foreground/70 z-10"
      >
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
};

const Ribbon = () => (
  <div data-testid="editorial-ribbon" className="border-y border-border py-5 overflow-hidden bg-secondary/40">
    <div className="animate-marquee flex whitespace-nowrap w-max">
      {[0, 1].map((n) => (
        <span key={n} className="font-serif text-xl md:text-2xl text-navy/70 italic flex items-center">
          {["Adventures in Learning", "Expertise in Life", "Small Classes", "Critical Thinking", "Hands-On Mastery"].map((t) => (
            <span key={t + n} className="flex items-center">
              <span className="px-8">{t}</span>
              <span className="text-gold text-sm">✦</span>
            </span>
          ))}
        </span>
      ))}
    </div>
  </div>
);

const Manifesto = () => (
  <section data-testid="manifesto-section" className="py-28 md:py-36">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <Overline>Our Manifesto</Overline>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-navy max-w-2xl leading-tight">
          Four commitments shape every classroom we open.
        </h2>
      </Reveal>
      <div className="mt-20 grid md:grid-cols-2 gap-x-16 gap-y-16">
        {MANIFESTO.map((ch, i) => (
          <Reveal key={ch.number} delay={i * 0.08} data-testid={`manifesto-chapter-${ch.number}`}>
            <div className="flex gap-8 items-start group">
              <span className="font-serif text-7xl md:text-8xl leading-none text-accent/30 transition-colors duration-500 group-hover:text-accent/60 select-none">
                {ch.number}
              </span>
              <div className="pt-3 border-t border-border flex-1">
                <h3 className="font-serif text-2xl md:text-3xl text-navy">{ch.title}</h3>
                <p className="mt-3 text-muted-foreground leading-relaxed">{ch.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProgramsShowcase = () => {
  const [active, setActive] = useState(0);
  return (
    <section data-testid="programs-showcase" className="py-28 md:py-36 bg-navy text-primary-foreground">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline light>Programs of Study</Overline>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight max-w-xl">
              Learn the craft. Earn the credential.
            </h2>
            <Link to="/programs" data-testid="showcase-all-programs" className="group inline-flex items-center gap-2 text-sm text-gold">
              All Programs
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7 divide-y divide-primary-foreground/10 border-y border-primary-foreground/10">
            {PROGRAMS.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.05} y={24}>
                <Link
                  to={`/programs#${p.id}`}
                  data-testid={`showcase-program-${p.id}`}
                  onMouseEnter={() => setActive(i)}
                  className="group flex items-center justify-between gap-6 py-6 transition-colors duration-300"
                >
                  <div className="flex items-baseline gap-6">
                    <span className="font-serif text-sm text-gold/70">{p.index}</span>
                    <div>
                      <h3 className="font-serif text-2xl md:text-3xl leading-snug transition-colors duration-300 group-hover:text-gold">
                        {p.title}
                      </h3>
                      <p className="mt-1 text-xs tracking-[0.18em] uppercase text-primary-foreground/40">
                        {p.category} · {p.duration.split("·")[0]}
                      </p>
                    </div>
                  </div>
                  <ArrowUpRight
                    size={20}
                    className="shrink-0 text-primary-foreground/30 transition-all duration-300 group-hover:text-gold group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </Link>
              </Reveal>
            ))}
          </div>
          <div className="hidden lg:block lg:col-span-5">
            <div className="sticky top-32 relative overflow-hidden rounded-sm aspect-[4/5]">
              <AnimatePresence mode="popLayout">
                <motion.img
                  key={active}
                  src={PROGRAMS[active].image}
                  alt={PROGRAMS[active].title}
                  data-testid="showcase-image"
                  initial={{ opacity: 0, scale: 1.06 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.7, ease: EASE }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
              <p className="absolute bottom-5 left-5 font-serif italic text-xl text-primary-foreground/90">
                {PROGRAMS[active].credential}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Counter = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 2.2,
      ease: EASE,
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return <span ref={ref}>{display.toLocaleString()}</span>;
};

const Stats = () => (
  <section data-testid="stats-section" className="py-24 border-b border-border">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-14">
      {STATS.map((s, i) => (
        <Reveal key={s.label} delay={i * 0.1} className={`px-6 ${i > 0 ? "lg:border-l lg:border-border" : ""}`}>
          <p className="font-serif text-5xl md:text-6xl text-navy" data-testid={`stat-value-${i}`}>
            <Counter value={s.value} />
            {s.value >= 1000 && <span className="text-accent">+</span>}
          </p>
          <p className="mt-3 text-xs tracking-[0.2em] uppercase text-muted-foreground">{s.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-28 md:py-36 overflow-hidden">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <Overline>Student Voices</Overline>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-navy leading-tight max-w-2xl">
          They arrived curious. They left capable.
        </h2>
      </Reveal>
    </div>
    <div className="mt-16 relative">
      <div className="animate-marquee-slow flex w-max gap-8 px-4 hover:[animation-play-state:paused]">
        {[...TESTIMONIALS, ...TESTIMONIALS].map((t, i) => (
          <figure
            key={i}
            data-testid={i < TESTIMONIALS.length ? `testimonial-card-${i}` : undefined}
            className="w-[340px] md:w-[420px] shrink-0 bg-secondary/50 border border-border rounded-sm p-8"
          >
            <blockquote className="font-serif italic text-lg md:text-xl leading-relaxed text-navy/90">
              “{t.quote}”
            </blockquote>
            <figcaption className="mt-6 flex items-center gap-3">
              <span className="h-px w-8 bg-gold" />
              <div>
                <p className="text-sm font-medium text-navy">{t.name}</p>
                <p className="text-xs text-muted-foreground">
                  {t.country} · {t.program}
                </p>
              </div>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const NewsPreview = () => (
  <section data-testid="news-section" className="py-28 md:py-36 bg-secondary/30 border-y border-border">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Overline>College Journal</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-navy leading-tight">
              News & stories from campus
            </h2>
          </div>
          <Link to="/news" data-testid="news-view-all" className="group inline-flex items-center gap-2 text-sm text-navy">
            <span className="border-b border-gold pb-0.5 transition-colors duration-300 group-hover:text-accent group-hover:border-accent">
              View All Stories
            </span>
            <ArrowUpRight size={15} />
          </Link>
        </div>
      </Reveal>
      <div className="mt-16 grid md:grid-cols-3 gap-8">
        {NEWS.slice(0, 3).map((a, i) => (
          <Reveal key={a.id} delay={i * 0.1}>
            <Link to="/news" data-testid={`news-card-${a.id}`} className="group block">
              <div className="overflow-hidden rounded-sm aspect-[4/3]">
                <img
                  src={a.image}
                  alt={a.title}
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                />
              </div>
              <p className="mt-5 text-xs tracking-[0.2em] uppercase text-accent">{a.category}</p>
              <h3 className="mt-2 font-serif text-2xl leading-snug text-navy transition-colors duration-300 group-hover:text-accent">
                {a.title}
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">by {a.author}</p>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const CTA = () => (
  <section data-testid="cta-section" className="py-28 md:py-36">
    <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
      <Reveal>
        <Overline>Admissions Open</Overline>
        <h2 className="mt-6 font-serif text-4xl md:text-6xl tracking-tight text-navy leading-[1.1]">
          Your craft is waiting.
          <br />
          <span className="italic text-accent">Begin the conversation.</span>
        </h2>
        <p className="mt-8 max-w-xl mx-auto text-muted-foreground leading-relaxed">
          Rolling start dates, classes of four to six, and funding that may be available
          for eligible students. Tell us where you want to go.
        </p>
        <Link
          to="/contact"
          data-testid="cta-apply-btn"
          className="group mt-10 inline-flex items-center gap-3 bg-navy text-primary-foreground px-9 py-4 rounded-sm text-sm tracking-wide transition-colors duration-300 hover:bg-accent"
        >
          Start Your Enquiry
          <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <main>
      <Hero />
      <Ribbon />
      <Manifesto />
      <ProgramsShowcase />
      <Stats />
      <Testimonials />
      <PhotoGallery />
      <NewsPreview />
      <CTA />
    </main>
  );
}
